<template>
  <transition-group name="toast" tag="div" class="toast-container">
    <div v-for="toast in toasts" :key="toast.id" class="toast-item" :class="'toast-' + toast.type">
      <div class="toast-accent"></div>
      <div class="toast-icon">
        <svg v-if="toast.type === 'success'" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="4 9 8 13 14 5" />
        </svg>
        <svg v-else width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="9" y1="5" x2="9" y2="10" />
          <circle cx="9" cy="13" r="0.5" fill="currentColor" />
        </svg>
      </div>
      <span class="toast-message">{{ toast.message }}</span>
      <button class="toast-dismiss" @click="dismiss(toast.id)" aria-label="Dismiss">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
          <line x1="3" y1="3" x2="11" y2="11" /><line x1="11" y1="3" x2="3" y2="11" />
        </svg>
      </button>
      <div class="toast-progress">
        <div class="toast-progress-bar" :class="'progress-' + toast.type" :style="{ animationDuration: toast.duration + 'ms' }"></div>
      </div>
    </div>
  </transition-group>
</template>

<script>
export default {
  name: 'ToastContainer',
  data() {
    return {
      toasts: [],
      counter: 0
    }
  },
  methods: {
    add(type, message) {
      const id = ++this.counter
      const duration = type === 'error' ? 6000 : 4000
      this.toasts.push({ id, type, message, duration })
      setTimeout(() => { this.dismiss(id) }, duration)
    },
    dismiss(id) {
      const idx = this.toasts.findIndex(t => t.id === id)
      if (idx !== -1) this.toasts.splice(idx, 1)
    }
  }
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 320px;
  max-width: 440px;
}

.toast-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 14px 14px;
  background: rgba(26, 26, 46, 0.92);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.24), 0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.04);
  overflow: hidden;
}

.toast-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
}

.toast-success .toast-accent {
  background: linear-gradient(to bottom, #48d22b, #36b81f);
}

.toast-error .toast-accent {
  background: linear-gradient(to bottom, #f87171, #c0392b);
}

.toast-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
}

.toast-success .toast-icon {
  background: rgba(72, 210, 43, 0.12);
  color: #6fcf73;
}

.toast-error .toast-icon {
  background: rgba(248, 113, 113, 0.12);
  color: #f87171;
}

.toast-message {
  flex: 1;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #e8e9f0;
  line-height: 1.4;
}

.toast-dismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: rgba(255,255,255,0.4);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  border-radius: 4px;
}

.toast-dismiss:hover {
  color: rgba(255,255,255,0.7);
  background: rgba(255,255,255,0.06);
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
}

.toast-progress-bar {
  height: 100%;
  transform-origin: left;
  animation: toast-deplete linear forwards;
}

.progress-success {
  background: linear-gradient(to right, rgba(72, 210, 43, 0.5), rgba(72, 210, 43, 0.15));
}

.progress-error {
  background: linear-gradient(to right, rgba(248, 113, 113, 0.5), rgba(248, 113, 113, 0.15));
}

@keyframes toast-deplete {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

/* Enter */
.toast-enter-from {
  opacity: 0;
  transform: translateX(40px) scale(0.96);
}
.toast-enter-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
}

/* Leave */
.toast-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.toast-leave-active {
  transition: all 0.28s cubic-bezier(0.4, 0, 1, 1);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-12px) scale(0.96);
}

/* Reorder */
.toast-move {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@media (max-width: 600px) {
  .toast-container {
    left: 12px;
    right: 12px;
    min-width: auto;
    max-width: none;
  }
}
</style>
